import React, { useState, useRef } from 'react';
import axios from 'axios';
import { documentAPI } from '../../services/api';

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

const documentTypeOptions = [
    { value: 1, label: 'Contract' },
    { value: 2, label: 'Permit' },
    { value: 3, label: 'Estimate' },
    { value: 4, label: 'Other' }
];

const DocumentUploadFiltered = ({ projectId, onUploadComplete }) => {
    const [files, setFiles] = useState([]);
    const [documentType, setDocumentType] = useState(4); // Default: Other
    const [uploading, setUploading] = useState(false);
    const [completed, setCompleted] = useState(0);
    const [total, setTotal] = useState(0);
    const [error, setError] = useState('');
    const fileInputRef = useRef(null);

    const handleFileChange = (e) => {
        const selected = Array.from(e.target.files);
        setError('');

        if (selected.length === 0) {
            setFiles([]);
            return;
        }

        const oversized = selected.filter(f => f.size > MAX_FILE_SIZE);
        if (oversized.length > 0) {
            setError(`${oversized.length} file(s) exceed 50MB limit and were removed`);
        }

        setFiles(selected.filter(f => f.size <= MAX_FILE_SIZE));
    };

    const uploadSingleFile = async (file) => {
        const { data: urlData } = await documentAPI.getUploadUrl({
            projectId,
            fileName: file.name,
            contentType: file.type || 'application/octet-stream',
            documentType
        });

        await axios.put(urlData.uploadUrl, file, {
            headers: { 'Content-Type': file.type || 'application/octet-stream' }
        });

        await documentAPI.saveMetadata({
            projectId,
            fileName: file.name,
            fileKey: urlData.fileKey,
            fileSize: file.size,
            contentType: file.type || 'application/octet-stream',
            documentType
        });
    };

    const handleUpload = async () => {
        if (files.length === 0) return;

        setUploading(true);
        setTotal(files.length);
        setCompleted(0);
        setError('');

        let failCount = 0;
        for (const file of files) {
            try {
                await uploadSingleFile(file);
            } catch (err) {
                console.error(`Failed to upload ${file.name}:`, err);
                failCount++;
            }
            setCompleted(prev => prev + 1);
        }

        if (failCount > 0) {
            setError(`${failCount} of ${files.length} uploads failed`);
        }

        setFiles([]);
        setDocumentType(4);
        setUploading(false);
        setCompleted(0);
        setTotal(0);
        if (fileInputRef.current) fileInputRef.current.value = '';
        if (onUploadComplete) onUploadComplete();
    };

    const buttonLabel = uploading
        ? `Uploading ${completed}/${total}...`
        : `Upload${files.length > 1 ? ` (${files.length} files)` : ''}`;

    return (
        <div className="doc-upload-filtered">
            <div className="doc-upload-controls">
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    className="doc-file-input"
                    disabled={uploading}
                    multiple
                />
                <select
                    value={documentType}
                    onChange={(e) => setDocumentType(parseInt(e.target.value))}
                    className="doc-type-select"
                    disabled={uploading}
                >
                    {documentTypeOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
                <button
                    onClick={handleUpload}
                    disabled={files.length === 0 || uploading}
                    className="doc-upload-btn"
                >
                    {buttonLabel}
                </button>
            </div>

            {uploading && total > 0 && (
                <div className="doc-progress-bar">
                    <div className="doc-progress-fill" style={{ width: `${Math.round((completed / total) * 100)}%` }} />
                    <span className="doc-progress-text">{completed}/{total}</span>
                </div>
            )}

            {error && <div className="doc-error">{error}</div>}
        </div>
    );
};

export default DocumentUploadFiltered;
